export interface ApiResponse<T = unknown> {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
}

interface ApiRequestOptions {
  headers?: HeadersInit;
  body?: unknown;
}

const getCSRFToken = (): string => {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';
};

const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {};
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...headers };
};

const buildHeaders = (body?: unknown, customHeaders?: HeadersInit): HeadersInit => {
  const csrfToken = getCSRFToken();
  const baseHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
  };

  if (!(body instanceof FormData)) {
    baseHeaders['Content-Type'] = 'application/json';
  }

  return {
    ...baseHeaders,
    ...normalizeHeaders(customHeaders),
  };
};

const parseResponseBody = async (response: Response): Promise<unknown | null> => {
  if (response.status === 204) return null;

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text.length > 0 ? text : null;
};

const getErrorMessage = (response: Response, body: unknown): string => {
  if (body && typeof body === 'object') {
    const maybeError = (body as { error?: string; message?: string });
    return maybeError.error || maybeError.message || response.statusText || 'Request failed';
  }
  return response.statusText || 'Request failed';
};

const request = async <T>(url: string, method: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> => {
  const requestBody =
    options.body instanceof FormData
      ? options.body
      : options.body === undefined
        ? undefined
        : JSON.stringify(options.body);

  const response = await fetch(url, {
    method,
    headers: buildHeaders(options.body, options.headers),
    body: requestBody,
    credentials: 'same-origin',
  });

  const data = (await parseResponseBody(response)) as T | null;

  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      data,
      error: getErrorMessage(response, data),
    };
  }

  return {
    ok: true,
    status: response.status,
    data,
    error: null,
  };
};

export const apiClient = {
  get: <T>(url: string, options: Omit<ApiRequestOptions, 'body'> = {}) =>
    request<T>(url, 'GET', options),
  post: <T>(url: string, body?: unknown, options: Omit<ApiRequestOptions, 'body'> = {}) =>
    request<T>(url, 'POST', { ...options, body }),
  put: <T>(url: string, body?: unknown, options: Omit<ApiRequestOptions, 'body'> = {}) =>
    request<T>(url, 'PUT', { ...options, body }),
  patch: <T>(url: string, body?: unknown, options: Omit<ApiRequestOptions, 'body'> = {}) =>
    request<T>(url, 'PATCH', { ...options, body }),
  delete: <T>(url: string, options: Omit<ApiRequestOptions, 'body'> = {}) =>
    request<T>(url, 'DELETE', options),
};
